<template>
  <div class="app-activation-top-up">
    <div class="text"><slot name="txtBeforeTopUp"></slot></div>
    <b-form
      class="activation-top-up-form"
      :id="'form' + _uid"
      :novalidate="true">
      <EnterAutoSingleTopUp v-if="isPUA"
        :autoTopUp.sync="autoTopUp"
        :singleTopUp.sync="singleTopUp"
        :selected-top-up-types.sync="selectedTopUpTypes"
        :monthly-value="monthlyValue"
        :possibleSingleTopUpAmounts="possibleSingleTopUpAmounts_"
        :showSingleTopUp="showSingleTopUp"
        :show-auto-top-up="showAutoTopUp"
        :selected-tariff="selectedTariff"
      >
        <template v-for="slot in possibleChildSlotsFor('AutoSingle')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </EnterAutoSingleTopUp>
      <EnterPaymentData v-show="paymentRequired"
                      :credit-card-redirect-url="creditCardRedirectUrl"
                      :credit-card-frame-address="creditCardFrameAddress"
                      :payment.sync="payment_"
                      :paymentInvalid.sync="paymentInvalid_"
                      :payment-context="paymentContext"
                      :process-name="'activation'"
                      :credit-card-possible="creditCardPossible"
      >
        <template v-for="slot in possibleChildSlotsFor('Payment')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </EnterPaymentData>
      <Stepper
        @prev="back()"
        @next="save()"
        :loading-next="loading"
        :disabledNext="$v.$invalid || loading"
        :show-prev="true"
      >
        <template v-for="slot in possibleChildSlotsFor('Stepper')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Stepper>
    </b-form>
  </div>
</template>

<script>
import EnterAutoSingleTopUp from './EnterAutoSingleTopUp.vue'
import EnterPaymentData from '../../Payment/EnterPaymentData.vue'
import { validationMixin } from 'vuelidate'
import { mapMutations, mapActions, mapGetters } from 'vuex'
import processSteps from '@/mixins/processSteps'
import slotProxy from '@/mixins/slotProxy'
import Stepper from '@/components/generic/Stepper.vue'
import preFilledObjectInput from '@/mixins/preFilledObjectInput'
import preFilledInput from '@/mixins/preFilledInput'
export const stepName = 'topUp'
export default {
  name: 'EnterTopUpAndPayment',
  data () {
    return {
      paymentInvalid_: true,
      loading: false
    }
  },
  mixins: [
    validationMixin,
    processSteps(stepName),
    preFilledObjectInput('payment', 'storedPayment'),
    preFilledObjectInput('autoTopUp', 'storedAutoTopUp'),
    preFilledInput('singleTopUp', 'storedSingleTopUp'),
    preFilledInput('selectedTopUpTypes', 'defaultSelectedTopUpTypes'),
    slotProxy()
  ],
  validations () {
    return {
      autoTopUp: {
        validType: (value, vm) => {
          return this.validAutoTopUp
        }
      },
      paymentInvalid_: {
        validPaymentIfRequired: (value, vm) => {
          if (vm.paymentRequired === false) {
            return true
          }
          return value === false
        }
      }
    }
  },
  computed: {
    // Payment
    // # Config
    ...mapGetters('activation/info', {
      possibleSingleTopUpAmounts: 'singleTopUpAmounts'
    }),
    ...mapGetters('activation/tariff', [
      'savedTariff'
    ]),
    possibleSingleTopUpAmounts_: function () {
      if (this.selectedTariff?.tariffExtra?.type === 'year') {
        const amounts = []
        amounts.default = this.selectedTariff.tariffExtra?.singleTopUp
        amounts.amount = [this.selectedTariff.tariffExtra?.singleTopUp]
        return amounts
      }
      return this.possibleSingleTopUpAmounts
    },
    showAutoTopUp: function () {
      return this.savedTariff?.tariffExtra?.allowAutoTopUp
    },
    showSingleTopUp: function () {
      return this.savedTariff?.tariffExtra?.allowTopUp
    },
    isPUA: function () {
      return this.savedTariff?.tariffExtra?.market === 'PUA'
    },
    // # Data
    ...mapGetters('activation/topUp', {
      storedAutoTopUp: 'topUp',
      storedSingleTopUp: 'singleTopUpAmount',
      storedSelectedTopUpTypes: 'selectedTopUpTypes'
    }),
    defaultSelectedTopUpTypes: function () {
      // if we have already stored a value, this gets first
      // otherwise, all AVAILABLE!!! types are preselected
      if (this.selectedTariff?.tariffExtra?.type === 'year') {
        return [
          ...((this.showSingleTopUp) ? ['single'] : [])
        ]
      }
      if (this.storedSelectedTopUpTypes.length === 0) {
        return [
          'auto',
          ...((this.showSingleTopUp) ? ['single'] : [])
        ]
      }
      return this.storedSelectedTopUpTypes
    },
    // # Validation
    validAutoTopUp: function () {
      return this.selectedTopUpTypes.includes('auto') === false || this.autoTopUp.type !== null
    },
    singleSelected: function () {
      return this.selectedTopUpTypes.includes('single')
    },
    autoSelected: function () {
      return this.selectedTopUpTypes.includes('auto')
    },
    // Payment
    // # Config
    ...mapGetters('activation/info', [
      'hasValidatedPPlusToken'
    ]),
    creditCardPossible: function () {
      // there is a requirement that credit card should not be possible when a valid pplus token is used and Tariff type == PUA
      return this.hasValidatedPPlusToken === false && this.isPUA
    },
    // # Data
    ...mapGetters('activation/payment', {
      paymentContext: 'paymentContext',
      storedPayment: 'payment'
    }),
    // # Validation
    paymentRequired: function () {
      return this.singleSelected || this.autoSelected
    },
    // stuff
    getTopUp () {
      return { ...this.storedAutoTopUp }
    },
    fullNameAccountOwner () {
      return this.paymentContext.firstName + ' ' + this.paymentContext.lastName
    }
  },
  methods: {
    ...mapMutations('activation', [
      'goToStep'
    ]),
    ...mapActions('activation/topUp', [
      'storeAllTopUpData'
    ]),
    ...mapActions('activation/submit', [
      'activationInitiate'
    ]),
    back () {
      this.goToStep(this.previousStep)
    },
    async save () {
      this.loading = true
      this.payment.owner = this.fullNameAccountOwner
      await this.storeAllTopUpData({
        selectedTopUpTypes: this.selectedTopUpTypes,
        single: this.singleTopUp,
        auto: this.autoTopUp,
        payment: this.payment,
        // no not change step on payment validation. only if initiate was successful we can securely show summary page
        nextStep: stepName
      })
        .then(() => {
          this.loading = false
          this.initiate()
        }, () => {
          this.loading = false
        })
    },
    initiate () {
      this.loading = true
      this.activationInitiate({
        nextStep: this.nextStep
      })
        .then(() => {
          this.loading = false
        })
      this.loading = false
    }
  },
  components: { EnterPaymentData, EnterAutoSingleTopUp, Stepper },
  props: {
    creditCardFrameAddress: {
      type: String
    },
    creditCardRedirectUrl: {
      type: String
    },
    monthlyValue: {
      type: Array
    },
    selectedTariff: {
      type: Object
    }
  }
}
</script>
